import React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { useForm } from 'react-hook-form';
import Joi from 'joi';
import { joiResolver } from '@hookform/resolvers/joi';
import { ESubSource } from '@quesmed/types-rn/models';

import { Modal } from 'components/Modal';
import { FormField } from 'components/TextField';
import { Link } from 'components/Link';
import { Body } from 'components/Typography';

const StyledInputWrapper = styled(Box)(({ theme: { spacing } }) => ({
  '& .MuiFormControl-root': {
    marginTop: spacing(6),
  },
}));

const DeviceCancelationContainer = styled(Box)(({ theme: { spacing } }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing(2),
  marginTop: spacing(4),
}));

interface SubscriptionCancelationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (comment: CancelationInput) => void;
  loading: boolean;
  purchaseSource: ESubSource;
}

export interface CancelationInput {
  comment?: string;
}

const schema = Joi.object<CancelationInput>({
  comment: Joi.string().max(255).messages({
    'string.max': 'Comment can include up to 255 characters',
  }),
});

export const SubscriptionCancelationModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  purchaseSource,
}: SubscriptionCancelationModalProps) => {
  const { control, handleSubmit } = useForm<CancelationInput>({
    defaultValues: {
      comment: '',
    },
    resolver: joiResolver(schema),
    mode: 'onTouched',
  });
  const isAppSubscription = purchaseSource === ESubSource.REVENUE_CAT;

  return (
    <Modal
      loading={loading}
      maxWidth="md"
      noPaddingY
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      open={isOpen}
      showCloseButton
      sizeVariant="md"
      submitLabel="Confirm"
      title="Are you sure you want to cancel subscription?"
    >
      <Body>
        If you&apos;re done with exams or finishing medical school,
        congratulations!
      </Body>
      <Body>
        Otherwise, is there any particular reason why you&apos;re cancelling?
        {isAppSubscription ? '*' : ''}
      </Body>
      <StyledInputWrapper>
        <FormField
          control={control}
          fullWidth
          label="Comment"
          multiline
          name="comment"
          rows={5}
          type="text"
        />
      </StyledInputWrapper>
      {isAppSubscription ? (
        <DeviceCancelationContainer>
          <sup>
            *Please note that if you subscribed via the app, please cancel
            through your device&apos;s platform:{' '}
            <Link to="https://support.apple.com/en-us/HT202039">iOS</Link>,{' '}
            <Link to="https://support.google.com/googleplay/answer/7018481">
              Android
            </Link>
          </sup>
          <sup>
            Cancelling on our website won&apos;t stop charges from Apple or
            Google.
          </sup>
        </DeviceCancelationContainer>
      ) : null}
    </Modal>
  );
};
